// A team's media cookies in this tab (spec assets/media-cookies, Design 4). `useMediaAccess` asks
// the API for them (`POST .../media-access`), renews them at each URL step and on waking, and
// tells `api/media-cookie.ts` how long they last, so a team's requests ask for each file's own URL
// (`Prefer: media=cookie`) only while its cookie is fresh. A picture that fails at its own URL is
// asked for once more after a renewal; failing again, the team reads signed URLs for the rest of
// the tab's life. With cookies off (`enabled: false`) or the API unreachable, nothing changes:
// every URL stays signed.
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { grantMediaAccess } from "../../api/generated/endpoints/assets/assets";
import {
  HEADER_MARGIN_MS,
  dropMediaCookie,
  forgetMediaCookie,
  holdMediaCookie,
  isBareMediaUrl,
  mediaCookieDropped,
  mediaCookieLeft,
  resetMediaCookies,
} from "../../api/media-cookie";
import {
  clearMediaGrants,
  forgetMediaGrant,
  readMediaGrant,
  readMediaGrantsOff,
  writeMediaGrant,
  writeMediaGrantsOff,
} from "../prefs";

/** The API's URL step: a renewal each step keeps a cookie between 60 and 75 minutes. */
export const RENEW_EVERY_MS = 15 * 60_000;
/** On waking (visible, online, focus), a cookie with less than this left is renewed at once. */
export const RENEW_BELOW_MS = 45 * 60_000;
/** The longest a team's first paint waits on its first grant. */
export const FIRST_GRANT_WAIT_MS = 3_000;

type MediaElement = HTMLImageElement | HTMLMediaElement;

/** Forgets every media grant of this tab, held and kept: a sign-in and a sign-out. */
export function forgetMediaAccess(): void {
  resetMediaCookies();
  clearMediaGrants();
}

function reload(element: MediaElement): void {
  if (element instanceof HTMLImageElement) {
    // Setting `src`, even to the same URL, asks for the picture again.
    element.setAttribute("src", element.src);
    return;
  }
  // A video or a sound resumes where it was, playing if it played.
  const at = element.currentTime;
  const playing = !element.paused;
  element.load();
  element.currentTime = at;
  if (playing) element.play().catch(() => undefined);
}

/** Whether `url` is a file's own URL under one of the cookies' paths. */
function underPaths(url: string, paths: readonly string[]): boolean {
  if (!isBareMediaUrl(url)) return false;
  const { pathname } = new URL(url, window.location.href);
  return paths.some((path) => pathname.startsWith(path));
}

/** Whether `userId`'s pages of `teamId` may render at once: on a grant this tab kept with more
 *  than 5 minutes left, which it holds before they render so their first requests ask for bare
 *  URLs on it, or on the word that cookies are off. */
function readyAtOnce(userId: string, teamId: string): boolean {
  const kept = readMediaGrant(userId, teamId);
  const fresh = kept !== null && kept - Date.now() >= HEADER_MARGIN_MS;
  if (fresh) holdMediaCookie(teamId, kept);
  return fresh || readMediaGrantsOff();
}

/**
 * Asks for `teamId`'s media cookies for `userId` and keeps them fresh while mounted. Returns
 * whether the team's pages may render: at once when this tab holds a grant with more than 5
 * minutes left, or knows cookies are off; else once the first answer arrives (a grant,
 * `enabled: false`, or an error), and after `FIRST_GRANT_WAIT_MS` at the latest. Once they may,
 * they stay. Its owner mounts it anew for each user and team (`TeamLayout` keys its frame), since
 * only a mount reads the kept grant; another team in the same mount waits for its answer.
 */
export function useMediaAccess(teamId: string, userId: string): boolean {
  const queryClient = useQueryClient();
  const key = `${userId}:${teamId}`;
  // The user and team whose pages may render; once they may, they stay.
  const [readyFor, setReadyFor] = useState(() => (readyAtOnce(userId, teamId) ? key : null));

  useEffect(() => {
    let alive = true;
    let off = false;
    let inFlight: Promise<void> | undefined;
    let renewals = 0;
    // The cookies' paths, from the last grant; the team's and the shared prefix until then.
    let paths: readonly string[] = [`/t/${teamId.toLowerCase()}/`, "/g/"];
    // Pictures that failed at their own URL, waiting for a renewal, and the renewal each was
    // last asked for again after.
    const waiting = new Set<MediaElement>();
    const retriedAt = new WeakMap<MediaElement, number>();
    const answer = () => {
      if (alive) setReadyFor(key);
    };
    // The team's answers are read again, so any bare URL they hold comes back signed. On the
    // switch to signed URLs (a drop, or cookies turned off) a read already under way asked for
    // bare ones, so it is cancelled and read anew without the header; after it, a burst of
    // failed pictures asks once, never cancelling the signed read under way.
    const readAgain = (switching: boolean) => {
      const prefix = `/api/v1/teams/${teamId}/`;
      void queryClient.invalidateQueries(
        {
          predicate: ({ queryKey: [first] }) =>
            typeof first === "string" && first.startsWith(prefix),
        },
        { cancelRefetch: switching },
      );
    };

    const renew = (): Promise<void> => {
      if (off || mediaCookieDropped(teamId)) {
        // Nothing to ask for: the team reads signed URLs, and its pages need not wait.
        answer();
        return Promise.resolve();
      }
      inFlight ??= grantMediaAccess(teamId)
        .then(
          (access) => {
            if (!alive) return;
            if (!access.enabled || access.expires_in === null) {
              // Cookies off: signed URLs, and nothing more to renew. A grant this tab held means
              // they were on until now, so what the team's answers hold is read again.
              off = true;
              writeMediaGrantsOff(true);
              const held = mediaCookieLeft(teamId) > 0 || waiting.size > 0;
              forgetMediaCookie(teamId);
              forgetMediaGrant(userId, teamId);
              waiting.clear();
              if (held) readAgain(true);
              return;
            }
            writeMediaGrantsOff(false);
            const endsAt = Date.now() + access.expires_in * 1000;
            holdMediaCookie(teamId, endsAt);
            writeMediaGrant(userId, teamId, endsAt);
            paths = access.paths;
            renewals += 1;
            for (const element of waiting) {
              retriedAt.set(element, renewals);
              reload(element);
            }
            waiting.clear();
          },
          () => {
            // Refused or unreachable: a grant still held stays until it ends; the next step or
            // waking asks again.
          },
        )
        .finally(() => {
          inFlight = undefined;
          answer();
        });
      return inFlight;
    };

    // A 403 at the CDN (a cookie run out while the tab slept, a planted one, a clock far off)
    // fails the element. Mid-playback, a Range request's 403 can surface as `stalled` instead;
    // with a fresh cookie, a stall is the network's, never the cookie's.
    const onFailure = (event: Event) => {
      const element = event.target;
      if (!(element instanceof HTMLImageElement || element instanceof HTMLMediaElement)) return;
      if (!underPaths(element.currentSrc || element.src, paths)) return;
      if (event.type === "stalled" && mediaCookieLeft(teamId) >= HEADER_MARGIN_MS) return;
      if (off || mediaCookieDropped(teamId)) {
        // A bare URL from before cookies went off, or before the team dropped to signed URLs:
        // its answers are read again, signed.
        readAgain(false);
        return;
      }
      if (retriedAt.get(element) === renewals) {
        // Failed again after a fresh cookie: signed URLs for the rest of this tab's life.
        dropMediaCookie(teamId);
        readAgain(true);
        return;
      }
      if (waiting.has(element)) return;
      waiting.add(element);
      void renew();
    };
    const onWake = () => {
      if (document.visibilityState === "visible" && mediaCookieLeft(teamId) < RENEW_BELOW_MS) {
        void renew();
      }
    };

    void renew();
    const firstPaint = window.setTimeout(answer, FIRST_GRANT_WAIT_MS);
    // Hidden tabs too: background playback keeps its cookie, and a waking tab's has 45 minutes.
    const step = window.setInterval(() => void renew(), RENEW_EVERY_MS);
    document.addEventListener("error", onFailure, true);
    document.addEventListener("stalled", onFailure, true);
    document.addEventListener("visibilitychange", onWake);
    window.addEventListener("online", onWake);
    window.addEventListener("focus", onWake);
    return () => {
      alive = false;
      window.clearTimeout(firstPaint);
      window.clearInterval(step);
      document.removeEventListener("error", onFailure, true);
      document.removeEventListener("stalled", onFailure, true);
      document.removeEventListener("visibilitychange", onWake);
      window.removeEventListener("online", onWake);
      window.removeEventListener("focus", onWake);
    };
  }, [key, teamId, userId, queryClient]);

  return readyFor === key;
}
